import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { NAV_LABELS } from '@/lib/vocabulary/securityTerminology';
import { computeAdminGeoConfidence } from '@/lib/security/adminGeoConfidence';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';

const DAYS_WINDOW = 30;

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

const normalizeCountry = (value: string) => value.trim().toUpperCase();

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

export default function DomainAccessPolicy() {
  const { domain, managedSite } = useOutletContext<{
    domain: { id: string; domain_name: string };
    managedSite?: { id?: string | null; site_url?: string | null; wp_admin_url?: string | null } | null;
  }>();
  const { user } = useAuth();
  const { data: isSuperAdmin } = useQuery({
    queryKey: ['isSuperAdmin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      if (error) throw error;
      return (data || []).some((row) => row.role === 'admin');
    },
    enabled: !!user?.id,
  });

  const [mode, setMode] = useState<'observe' | 'enforce'>('observe');
  const [allowCountries, setAllowCountries] = useState<string[]>([]);
  const [denyCountries, setDenyCountries] = useState<string[]>([]);
  const [allowInput, setAllowInput] = useState('');
  const [denyInput, setDenyInput] = useState('');
  const [learningWindowDays, setLearningWindowDays] = useState(30);
  const [applyTo, setApplyTo] = useState({
    wp_login: true,
    wp_admin: true,
    xmlrpc: false,
    rest_sensitive: false,
  });
  const [saving, setSaving] = useState(false);
  const [showLowConfirm, setShowLowConfirm] = useState(false);
  const [lowConfirmChecked, setLowConfirmChecked] = useState(false);
  const [bypassLoading, setBypassLoading] = useState(false);
  const [bypassToken, setBypassToken] = useState('');
  const [bypassExpiresAt, setBypassExpiresAt] = useState('');
  const [supportDebugBusy, setSupportDebugBusy] = useState(false);
  const [supportDebugTtl, setSupportDebugTtl] = useState(30);
  const [loginRequired, setLoginRequired] = useState(false);

  const since = useMemo(() => {
    const date = new Date(Date.now() - DAYS_WINDOW * 24 * 60 * 60 * 1000);
    return date.toISOString();
  }, []);

  const policyQuery = useQuery({
    queryKey: ['site-policy', managedSite?.id],
    queryFn: async () => {
      if (!managedSite?.id) return null;
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        setLoginRequired(true);
        throw new Error('Not authenticated');
      }
      setLoginRequired(false);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');
      const response = await fetch(
        `${supabaseUrl}/functions/v1/site-policy?site_id=${managedSite.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-SS-Auth': 'user',
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (response.status === 403) {
        console.warn('[site-policy] forbidden response', payload);
      }
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load policy');
      }
      return payload;
    },
    enabled: !!managedSite?.id,
  });

  useEffect(() => {
    const policy = policyQuery.data?.policy?.security_controls?.admin_geo_guard;
    if (!policy) return;
    setMode(policy.mode === 'enforce' ? 'enforce' : 'observe');
    setAllowCountries(Array.isArray(policy.allow_countries) ? policy.allow_countries : []);
    setDenyCountries(Array.isArray(policy.deny_countries) ? policy.deny_countries : []);
    const windowDays = Number(policy.learning_window_days ?? 30);
    setLearningWindowDays(Number.isFinite(windowDays) ? windowDays : 30);
    setApplyTo({
      wp_login: policy.apply_to?.wp_login !== false,
      wp_admin: policy.apply_to?.wp_admin !== false,
      xmlrpc: Boolean(policy.apply_to?.xmlrpc),
      rest_sensitive: Boolean(policy.apply_to?.rest_sensitive),
    });
  }, [policyQuery.data]);
  const enforcementGlobal = Boolean(
    policyQuery.data?.policy?.security_controls?.admin_geo_guard?.enforcement_enabled_globally
  );
  const supportDebug = policyQuery.data?.supportDebug;
  const supportDebugEnabled = Boolean(supportDebug?.enabled);

  const logsQuery = useQuery({
    queryKey: ['admin-geo-guard', domain.id, since],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_logs')
        .select('id, created_at, event_type, country, metadata')
        .eq('domain_id', domain.id)
        .gte('created_at', since)
        .in('event_type', ['admin_geo_observed', 'admin_geo_blocked', 'admin_geo_allowed'])
        .order('created_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data || [];
    },
  });

  const observations = logsQuery.data || [];

  const adminGeoEvents = useMemo(
    () =>
      observations.map((log) => {
        const metadata = toRecord(log.metadata);
        return {
          event_type: log.event_type,
          country: log.country,
          reason: typeof metadata?.reason === 'string' ? metadata.reason : null,
          metadata,
        };
      }),
    [observations]
  );

  const countryStats = useMemo(() => {
    const map = new Map<string, { country: string; count: number; success: number; lastSeen: string }>();
    let known = 0;
    let unknown = 0;
    observations.forEach((log: any) => {
      if (log.event_type !== 'admin_geo_observed') return;
      const rawCountry = (log.country || log.metadata?.country || '') as string;
      const country = rawCountry ? rawCountry : 'Unknown';
      const key = country;
      const attempt = log.metadata?.attempt || '';
      const prev = map.get(key) || { country, count: 0, success: 0, lastSeen: log.created_at };
      prev.count += 1;
      if (attempt === 'login_success') prev.success += 1;
      if (log.created_at && new Date(log.created_at) > new Date(prev.lastSeen)) {
        prev.lastSeen = log.created_at;
      }
      map.set(key, prev);
      if (rawCountry) {
        known += 1;
      } else {
        unknown += 1;
      }
    });
    const rows = Array.from(map.values()).sort((a, b) => b.count - a.count);
    return { rows, known, unknown };
  }, [observations]);

  const coverage = useMemo(() => {
    const total = countryStats.known + countryStats.unknown;
    if (total === 0) return { knownPct: 0, unknownPct: 0, total: 0 };
    const knownPct = Math.round((countryStats.known / total) * 100);
    return { knownPct, unknownPct: 100 - knownPct, total };
  }, [countryStats]);

  const confidence = useMemo(() => computeAdminGeoConfidence(adminGeoEvents), [adminGeoEvents]);
  const isLowConfidence = confidence.level === 'LOW';
  const isStagingEnv =
    import.meta.env.VITE_SHADOWSCAN_ENV === 'staging' || import.meta.env.MODE !== 'production';
  const showTestingHelpers = Boolean(isSuperAdmin) && (isStagingEnv || supportDebugEnabled);

  const handleSupportDebugEnable = async (ttlMinutes: number) => {
    if (!isSuperAdmin) return;
    setSupportDebugBusy(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      const response = await fetch(`${supabaseUrl}/functions/v1/support-debug-enable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domainId: domain.id, ttlMinutes }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.error) {
        throw new Error(result?.error || 'Unable to enable support debug');
      }
      toast.success('Support Debug enabled.');
      await policyQuery.refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Unable to enable support debug');
    } finally {
      setSupportDebugBusy(false);
    }
  };

  const handleSupportDebugDisable = async () => {
    if (!isSuperAdmin) return;
    setSupportDebugBusy(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      const response = await fetch(`${supabaseUrl}/functions/v1/support-debug-disable`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domainId: domain.id }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.error) {
        throw new Error(result?.error || 'Unable to disable support debug');
      }
      toast.success('Support Debug disabled.');
      await policyQuery.refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Unable to disable support debug');
    } finally {
      setSupportDebugBusy(false);
    }
  };

  const handleSave = async () => {
    if (!managedSite?.id) {
      toast.error('Connect the domain before editing settings.');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'enforce' && allowCountries.length === 0) {
        throw new Error('Enforcement requires at least one allowed location.');
      }

      const payload = {
        site_id: managedSite.id,
        policy: {
          ...(policyQuery.data?.policy ?? {}),
          security_controls: {
            ...(policyQuery.data?.policy?.security_controls ?? {}),
            admin_geo_guard: {
              mode,
              allow_countries: allowCountries,
              deny_countries: denyCountries,
              learning_window_days: learningWindowDays,
              apply_to: applyTo,
              bypass: { enabled: true, ttl_minutes: 30 },
            },
          },
        },
      };

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        setLoginRequired(true);
        throw new Error('Not authenticated');
      }
      setLoginRequired(false);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/site-policy?site_id=${managedSite.id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-SS-Auth': 'user',
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (response.status === 403) {
        console.warn('[site-policy] forbidden response', result);
      }
      if (!response.ok || result?.error) {
        throw new Error(result?.error || 'Unable to update access policy');
      }
      toast.success(mode === 'enforce' ? 'Admin access rules saved (enforce enabled).' : 'Admin access rules saved (observe-only).');
      await policyQuery.refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Unable to update access policy');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBypass = async () => {
    if (!managedSite?.id) {
      toast.error('Connect the domain before generating a bypass link.');
      return;
    }
    const ok = window.confirm(
      'Anyone with this link can access wp-admin/wp-login until expiry. Continue?'
    );
    if (!ok) return;
    setBypassLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-geo-bypass', {
        method: 'POST',
        body: { domain_id: domain.id },
      });
      if (error) throw new Error(error.message || 'Unable to generate bypass link');
      if (data?.error) throw new Error(data.error);
      setBypassToken(data?.token || '');
      setBypassExpiresAt(data?.expires_at || '');
      toast.success('Emergency bypass link generated.');
    } catch (err: any) {
      toast.error(err?.message || 'Unable to generate bypass link');
    } finally {
      setBypassLoading(false);
    }
  };

  const adminBaseUrl = managedSite?.wp_admin_url || managedSite?.site_url || '';
  const resolvedLoginUrl = useMemo(() => {
    if (!adminBaseUrl) return '';
    if (/\/wp-login\.php\/?$/.test(adminBaseUrl)) {
      return adminBaseUrl;
    }
    if (/\/wp-admin\/?$/.test(adminBaseUrl)) {
      return adminBaseUrl.replace(/\/wp-admin\/?$/, '/wp-login.php');
    }
    return adminBaseUrl.replace(/\/$/, '') + '/wp-login.php';
  }, [adminBaseUrl]);
  const bypassUrl = bypassToken && resolvedLoginUrl
    ? `${resolvedLoginUrl}${resolvedLoginUrl.includes('?') ? '&' : '?'}ss_bypass=${bypassToken}`
    : '';

  const addAllowCountry = () => {
    const code = normalizeCountry(allowInput);
    if (!/^[A-Z]{2}$/.test(code)) {
      toast.error('Use a valid two-letter country code.');
      return;
    }
    setAllowCountries((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setAllowInput('');
  };

  const removeAllowCountry = (code: string) => {
    setAllowCountries((prev) => prev.filter((item) => item !== code));
  };

  const addDenyCountry = () => {
    const code = normalizeCountry(denyInput);
    if (!/^[A-Z]{2}$/.test(code)) {
      toast.error('Use a valid two-letter country code.');
      return;
    }
    setDenyCountries((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setDenyInput('');
  };

  const removeDenyCountry = (code: string) => {
    setDenyCountries((prev) => prev.filter((item) => item !== code));
  };

  if (loginRequired) {
    return (
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Admin Access Location Rules</CardTitle>
          <CardDescription>Sign in to view and manage admin access rules.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Your session has expired. Please sign in again to continue.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/60">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Admin Access Location Rules</CardTitle>
            <CardDescription>
              Limit where administrators can sign in from. ShadowScan monitors wp-admin and wp-login locations and can enforce allowlists when enabled.
            </CardDescription>
          </div>
          <Badge variant="outline">Monitoring</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isSuperAdmin && (
          <div className="rounded border border-border/60 bg-background/40 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">Support Debug Override</h3>
                <p className="text-xs text-muted-foreground">
                  Unlocks read-only debug panels in the plugin for this domain. Does not change enforcement.
                </p>
              </div>
              <Badge variant={supportDebugEnabled ? 'default' : 'secondary'}>
                {supportDebugEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {supportDebugEnabled
                ? `Enabled until ${formatTimestamp(supportDebug?.enabledUntil)}`
                : 'Disabled by default. Use for time-limited verification only.'}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-muted-foreground">Enable for</label>
              <select
                className="rounded border border-border bg-background px-2 py-1 text-xs"
                value={supportDebugTtl}
                onChange={(event) => setSupportDebugTtl(Number(event.target.value))}
                disabled={supportDebugBusy}
              >
                <option value={30}>30m</option>
                <option value={60}>1h</option>
                <option value={240}>4h</option>
                <option value={1440}>24h</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSupportDebugEnable(supportDebugTtl)}
                disabled={supportDebugBusy}
              >
                Enable
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleSupportDebugDisable}
                disabled={supportDebugBusy || !supportDebugEnabled}
              >
                Disable now
              </Button>
            </div>
          </div>
        )}
        {mode === 'enforce' && isLowConfidence && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
            Enforcement is enabled, but location confidence is LOW. Many requests lack location headers, so rules may not behave predictably.
          </div>
        )}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Mode</h3>
              <p className="text-xs text-muted-foreground">Observe never blocks access. Enforce blocks locations outside your allowlist.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Observe</span>
              <Switch
                checked={mode === 'enforce'}
                onCheckedChange={(value) => {
                  if (value) {
                    if (isLowConfidence) {
                      setShowLowConfirm(true);
                      return;
                    }
                    const ok = window.confirm(
                      'Enable enforcement? This will block wp-admin/wp-login access outside your allowlist.'
                    );
                    if (!ok) return;
                    setMode('enforce');
                  } else {
                    setMode('observe');
                  }
                }}
              />
              <span className="text-xs text-muted-foreground">Enforce</span>
            </div>
          </div>
          {showLowConfirm && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100 space-y-2">
              <div>
                Location confidence is LOW. Enforcement may not behave predictably while headers are missing.
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={lowConfirmChecked}
                  onChange={(event) => setLowConfirmChecked(event.target.checked)}
                />
                I understand the risks and want to enable enforcement.
              </label>
              <Button
                size="sm"
                variant="outline"
                disabled={!lowConfirmChecked}
                onClick={() => {
                  setMode('enforce');
                  setShowLowConfirm(false);
                  setLowConfirmChecked(false);
                }}
              >
                Enable enforcement
              </Button>
            </div>
          )}
          <div className="rounded border border-dashed border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            Enforcement applies only to wp-login and wp-admin. Unknown locations are allowed (fail-open).
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Allowed locations (observe mode)</h3>
          <p className="text-xs text-muted-foreground">
            These locations are used when enforcement is enabled. ShadowScan will keep observing in observe mode.
          </p>
          <div className="flex flex-wrap gap-2">
            {allowCountries.length === 0 && (
              <span className="text-xs text-muted-foreground">No allowlist set yet.</span>
            )}
            {allowCountries.map((code) => (
              <Badge key={code} variant="secondary" className="text-xs flex items-center gap-2">
                {code}
                <button type="button" onClick={() => removeAllowCountry(code)} className="text-[10px] text-muted-foreground">
                  ×
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Add country code (e.g., AU)"
              value={allowInput}
              onChange={(event) => setAllowInput(event.target.value)}
              className="w-48"
            />
            <Button size="sm" variant="outline" onClick={addAllowCountry}>
              Add
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAllowCountries(['AU', 'NZ'])}>
              Add Australia + New Zealand
            </Button>
          </div>
          <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
            Use observe mode to learn locations. Enforce mode requires at least one allowed location.
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Deny locations (optional)</h3>
          <p className="text-xs text-muted-foreground">
            Deny lists are optional. They will be evaluated only when enforcement is enabled.
          </p>
          <div className="flex flex-wrap gap-2">
            {denyCountries.length === 0 && (
              <span className="text-xs text-muted-foreground">No denylist set.</span>
            )}
            {denyCountries.map((code) => (
              <Badge key={code} variant="outline" className="text-xs flex items-center gap-2">
                {code}
                <button type="button" onClick={() => removeDenyCountry(code)} className="text-[10px] text-muted-foreground">
                  ×
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Add country code (e.g., RU)"
              value={denyInput}
              onChange={(event) => setDenyInput(event.target.value)}
              className="w-48"
            />
            <Button size="sm" variant="outline" onClick={addDenyCountry}>
              Add
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDenyCountries([])}>
              Clear
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Draft policy settings</h3>
          <div className="grid gap-2 text-xs text-muted-foreground">
            <div>Mode: {mode}</div>
            <div>Learning window: {learningWindowDays} days</div>
            <div>
              Apply to: {applyTo.wp_login ? 'wp-login ' : ''}{applyTo.wp_admin ? 'wp-admin ' : ''}{applyTo.xmlrpc ? 'xmlrpc ' : ''}{applyTo.rest_sensitive ? 'REST sensitive' : ''}
            </div>
            <div>Allowlist size: {allowCountries.length}</div>
            <div>Denylist size: {denyCountries.length}</div>
          </div>
          <Button onClick={handleSave} disabled={saving || !managedSite?.id} variant="outline" size="sm">
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Observed admin locations (last {DAYS_WINDOW} days)</h3>
              <p className="text-xs text-muted-foreground">Based on wp-admin and wp-login activity. Locations are approximate.</p>
            </div>
            <Button asChild size="sm" variant="outline">
              <Link to={`/portal/domains/${domain.id}/logs`}>View Security Activity</Link>
            </Button>
          </div>

          {logsQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading observations…</p>
          ) : countryStats.rows.length === 0 ? (
            <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
              No location data yet. If you are behind Cloudflare, enable location headers. ShadowScan will continue monitoring when headers are present.
            </div>
          ) : (
            <div className="space-y-3">
                <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground space-y-2">
                  <div>
                    Location coverage: {coverage.knownPct}% known · {coverage.unknownPct}% unknown ({coverage.total} observations)
                  </div>
                  <div>
                    Location confidence: <span className="font-semibold">{confidence.level}</span>
                  </div>
                <div className="text-muted-foreground">
                  {confidence.level === 'HIGH' && 'Most admin traffic includes location headers.'}
                  {confidence.level === 'MEDIUM' && 'Some admin traffic includes location headers.'}
                  {confidence.level === 'LOW' && confidence.reason === 'insufficient_data' && 'Not enough data yet to assess reliability.'}
                  {confidence.level === 'LOW' && confidence.reason === 'missing_headers' && 'Location headers are often missing; enforcement may not work reliably.'}
                </div>
                {confidence.level === 'LOW' && (
                  <details className="text-muted-foreground">
                    <summary className="cursor-pointer text-xs">How to improve location detection</summary>
                    <div className="mt-2 text-xs">
                      If you use Cloudflare, ensure the CF-IPCountry header is passed to your origin. Otherwise, configure a trusted edge to inject X-ShadowScan-Country.
                    </div>
                  </details>
                )}
              </div>
                <div className="rounded border border-border/50">
              <div className="grid grid-cols-4 gap-2 border-b border-border/50 px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>Country</span>
                <span>Admin attempts</span>
                <span>Successful sign-ins</span>
                <span>Last seen</span>
              </div>
              <div className="divide-y divide-border/30">
                {countryStats.rows.map((entry) => (
                  <div key={entry.country} className="grid grid-cols-4 gap-2 px-3 py-2 text-xs">
                    <span>{entry.country}</span>
                    <span>{entry.count}</span>
                    <span>{entry.success}</span>
                    <span className="text-muted-foreground">{formatTimestamp(entry.lastSeen)}</span>
                  </div>
                ))}
              </div>
            </div>
            </div>
          )}
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Emergency access</h3>
              <p className="text-xs text-muted-foreground">
                Generate a time-limited bypass link if you are locked out due to location rules.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={handleGenerateBypass} disabled={bypassLoading}>
              {bypassLoading ? 'Generating…' : 'Generate emergency bypass link'}
            </Button>
          </div>
        {bypassToken && (
            <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground space-y-2">
              <div className="font-semibold text-foreground">Bypass token (expires {formatTimestamp(bypassExpiresAt)})</div>
              {bypassUrl ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={bypassUrl} readOnly className="flex-1 min-w-[240px]" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(bypassUrl);
                      toast.success('Bypass link copied.');
                    }}
                  >
                    Copy link
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={bypassToken} readOnly className="flex-1 min-w-[240px]" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(bypassToken);
                      toast.success('Bypass token copied.');
                    }}
                  >
                    Copy token
                  </Button>
                </div>
              )}
              {!bypassUrl && (
                <div>
                  Open your wp-login page and append <span className="font-mono">?ss_bypass=&lt;token&gt;</span>.
                </div>
              )}
              <div>One-time use. Expires in 30 minutes unless otherwise shown.</div>
              <div>Treat this link like a password.</div>
              <div>Issuance and usage are recorded in Security Activity.</div>
            </div>
          )}
        </div>

        <details className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-semibold text-foreground">Locked out of wp-admin?</summary>
          <div className="mt-2 space-y-1">
            <div>Step 1: Generate an emergency bypass link.</div>
            <div>Step 2: Use it once to regain access.</div>
            <div>Step 3: Review allowed locations.</div>
            <div>Step 4: Contact ShadowScan support if needed.</div>
          </div>
        </details>

        {showTestingHelpers && (
          <>
            <Separator />
            <details className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer font-semibold text-foreground">Testing helpers (internal)</summary>
              <div className="mt-3 space-y-2">
                <div>site_url: {managedSite?.site_url || 'Unknown'}</div>
                <div>wp_admin_url: {managedSite?.wp_admin_url || 'Unknown'}</div>
                <div>wp-login URL: {resolvedLoginUrl || 'Unknown'}</div>
                {resolvedLoginUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(resolvedLoginUrl);
                      toast.success('wp-login URL copied.');
                    }}
                  >
                    Copy wp-login URL
                  </Button>
                )}
                <div>Enforcement: {mode === 'enforce' ? 'Enforce' : 'Observe'}</div>
                <div>Global enforcement flag: {enforcementGlobal ? 'Enabled' : 'Disabled'}</div>
                <div>Allowlist: {allowCountries.length ? allowCountries.join(', ') : 'None'}</div>
                <div>Bypass append snippet: ?ss_bypass={bypassToken || 'TOKEN'}</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`?ss_bypass=${bypassToken || 'TOKEN'}`);
                    toast.success('Bypass snippet copied.');
                  }}
                >
                  Copy bypass snippet
                </Button>
              </div>
            </details>
          </>
        )}

        <Separator />

        <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
          Enforcement applies only to wp-login and wp-admin and fails open when location headers are missing.
        </div>
      </CardContent>
    </Card>
  );
}
